import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Lock, Mail, Settings, CheckCircle, AlertCircle, Loader2, Bell, Trash2 } from 'lucide-react';
import { apiService } from '../services/api';
import type { AlertDigestSubscription } from '../types';

interface UserData {
  id: number;
  email: string;
  title?: string;
  role: string;
  organization?: {
    name: string;
  };
  created_at: string;
  marketing_opt_out: boolean;
}

const AccountSettings: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Profile form state
  const [email, setEmail] = useState('');
  const [title, setTitle] = useState('');
  const [profileLoading, setProfileLoading] = useState(false);

  // Password form state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);

  // Marketing preferences
  const [marketingOptOut, setMarketingOptOut] = useState(false);
  const [marketingLoading, setMarketingLoading] = useState(false);

  // Alert digest (emerging + NCCN)
  const [digestSubs, setDigestSubs] = useState<AlertDigestSubscription[]>([]);

  useEffect(() => {
    fetchUserData();
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await apiService.getAlertDigestSubscriptions();
        if (res.success && res.subscriptions) setDigestSubs(res.subscriptions);
      } catch {
        // ignore
      }
    };
    if (userData) load();
  }, [userData]);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      const data = await apiService.getCurrentUser();
      setUserData(data);
      setEmail(data.email || '');
      setTitle(data.title || '');
      setMarketingOptOut(data.marketing_opt_out || false);
    } catch (err: any) {
      console.error('Error fetching user data:', err);
      setError('Failed to load account information.');
      if (err.response?.status === 401) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setProfileLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await apiService.updateProfile({ email, title });
      setSuccess('Profile updated successfully!');
      await fetchUserData(); // Refresh user data
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update profile.');
    } finally {
      setProfileLoading(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordLoading(true);
    setError(null);
    setSuccess(null);

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match.');
      setPasswordLoading(false);
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long.');
      setPasswordLoading(false);
      return;
    }

    try {
      await apiService.changePassword({
        current_password: currentPassword,
        new_password: newPassword,
      });
      setSuccess('Password changed successfully!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to change password.');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleRemoveDigestSub = async (id: number) => {
    try {
      await apiService.removeAlertDigestSubscription(id);
      setDigestSubs((prev) => prev.filter((s) => s.id !== id));
      setSuccess('Alert removed.');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to remove alert.');
    }
  };

  const handleMarketingUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setMarketingLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await apiService.updateMarketingPreferences({ marketing_opt_out: marketingOptOut });
      setSuccess(marketingOptOut ? 'You have opted out of marketing emails.' : 'You have opted in to marketing emails.');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update email preferences.');
    } finally {
      setMarketingLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold text-slate-900 mb-8 text-center">Account Settings</h1>

        {/* Success/Error Messages */}
        {success && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-green-700">{success}</p>
          </div>
        )}

        {error && (
          <div className="mb-6 bg-rose-50 border border-rose-200 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-rose-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-rose-700">{error}</p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Profile Information Section */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-slate-900 mb-6 flex items-center gap-2">
              <User className="w-5 h-5" />
              Profile Information
            </h2>

            <form onSubmit={handleProfileUpdate} className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  required
                />
              </div>

              <div>
                <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
                  Title
                </label>
                <input
                  id="title"
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Your Title (e.g., Analyst, VP)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              {userData && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  {userData.title && (
                    <p className="text-sm text-gray-600 mb-1">
                      <strong>Title:</strong> {userData.title}
                    </p>
                  )}
                  <p className="text-sm text-gray-600 mb-1">
                    <strong>Role:</strong> {userData.role.charAt(0).toUpperCase() + userData.role.slice(1)}
                  </p>
                  {userData.organization && (
                    <p className="text-sm text-gray-600 mb-1">
                      <strong>Organization:</strong> {userData.organization.name}
                    </p>
                  )}
                  <p className="text-sm text-gray-600">
                    <strong>Member since:</strong> {new Date(userData.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                  </p>
                </div>
              )}

              <button
                type="submit"
                disabled={profileLoading}
                className="w-full px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {profileLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Updating...
                  </>
                ) : (
                  'Update Profile'
                )}
              </button>
            </form>
          </div>

          {/* Change Password Section */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-slate-900 mb-6 flex items-center gap-2">
              <Lock className="w-5 h-5" />
              Change Password
            </h2>

            <form onSubmit={handlePasswordChange} className="space-y-4">
              <div>
                <label htmlFor="current_password" className="block text-sm font-medium text-gray-700 mb-2">
                  Current Password
                </label>
                <input
                  id="current_password"
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  required
                />
              </div>

              <div>
                <label htmlFor="new_password" className="block text-sm font-medium text-gray-700 mb-2">
                  New Password
                </label>
                <input
                  id="new_password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  required
                />
              </div>

              <div>
                <label htmlFor="confirm_password" className="block text-sm font-medium text-gray-700 mb-2">
                  Confirm New Password
                </label>
                <input
                  id="confirm_password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  required
                />
              </div>

              <div className="bg-blue-50 p-3 rounded-lg">
                <h3 className="text-sm font-medium text-blue-800 mb-2">Password Requirements:</h3>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• Use a strong, unique password</li>
                  <li>• Include a mix of letters, numbers, and symbols</li>
                  <li>• Avoid using personal information</li>
                </ul>
              </div>

              <button
                type="submit"
                disabled={passwordLoading}
                className="w-full px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {passwordLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Changing...
                  </>
                ) : (
                  'Change Password'
                )}
              </button>
            </form>
          </div>

          {/* Email Preferences Section */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 lg:col-span-2">
            <h2 className="text-xl font-semibold text-slate-900 mb-6 flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Email Preferences
            </h2>

            <form onSubmit={handleMarketingUpdate} className="space-y-4">
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <div className="flex items-center h-5">
                    <input
                      id="marketing_opt_out"
                      type="checkbox"
                      checked={marketingOptOut}
                      onChange={(e) => setMarketingOptOut(e.target.checked)}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                  </div>
                  <div className="flex-1">
                    <label htmlFor="marketing_opt_out" className="text-sm font-medium text-gray-700">
                      Opt out of marketing emails
                    </label>
                    <p className="text-sm text-gray-500 mt-1">
                      Check this box to stop receiving marketing updates, newsletters, promotional emails, and the
                      daily &quot;New data available&quot; summary (emerging / FDA / Prime) when there is new content
                      in the last 24 hours. You will still receive important account notifications and transactional
                      emails.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-4">
                <button
                  type="submit"
                  disabled={marketingLoading}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {marketingLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    'Update Email Preferences'
                  )}
                </button>
                {userData && (
                  <span className={`text-sm font-medium ${userData.marketing_opt_out ? 'text-orange-600' : 'text-green-600'}`}>
                    {userData.marketing_opt_out ? 'Currently opted out' : 'Currently opted in'}
                  </span>
                )}
              </div>
            </form>
          </div>

          {/* New data email (emerging indications you track) */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 lg:col-span-2">
            <h2 className="text-xl font-semibold text-slate-900 mb-6 flex items-center gap-2">
              <Bell className="w-5 h-5" />
              New data email
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              Once per day (~6:00 AM Eastern), if you are not opted out of marketing emails, we send a
              &quot;New data available&quot; message when something changed in the last 24 hours: emerging therapy
              activity for indications you track below, new FDA approvals (when your organization has access), and new
              Prime reports. Here you only manage your own email preferences and which indications to include—org-wide
              delivery is controlled by Ruby administrators, not from this screen.
            </p>
            {digestSubs.filter((s) => s.subscription_type === 'emerging').length > 0 && (
              <ul className="space-y-3 mb-6">
                {digestSubs
                  .filter((s) => s.subscription_type === 'emerging')
                  .map((s) => (
                    <li
                      key={s.id}
                      className="flex flex-wrap items-center justify-between gap-2 py-2 px-3 bg-gray-50 rounded-lg"
                    >
                      <span className="font-medium text-slate-800">
                        Emerging: {s.indication_name || s.indication_code || '—'}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleRemoveDigestSub(s.id)}
                        className="p-1.5 text-rose-600 hover:bg-rose-50 rounded"
                        title="Stop tracking this indication in email"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </li>
                  ))}
              </ul>
            )}
            <p className="text-xs text-gray-500 mt-1">
              To add an indication, open Emerging data, select it, and use &quot;Track for email updates&quot;.
            </p>
          </div>
        </div>

        {/* Back to Dashboard */}
        <div className="mt-8 text-center">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

export default AccountSettings;
